import React from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ColorValue,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { BlurView } from "expo-blur";
import { FontFamilies } from "@/constants/Typography";
import { CustomNotificationToast } from "./CustomNotificationToast";
import { SCREEN_WIDTH } from "@/constants/Sizes";

export type ToastType = "success" | "error" | "info" | "warning" | "confirm";

interface CustomToastProps {
  title: string;
  message: string;
  type: ToastType;
  onPress?: () => void;
}

const getToastConfig = (type: ToastType) => {
  switch (type) {
    case "success":
      return {
        iconColor: "#10B981",
        iconBgColor: "rgba(16, 185, 129, 0.1)",
        iconBorderColor: "rgba(16, 185, 129, 0.2)",
        gradientColors: [
          "rgba(240, 253, 244, 0.95)",
          "rgba(220, 252, 231, 0.85)",
        ],
        titleColor: "#065F46",
        messageColor: "#047857",
      };
    case "error":
      return {
        iconColor: "#EF4444",
        iconBgColor: "rgba(239, 68, 68, 0.1)",
        iconBorderColor: "rgba(239, 68, 68, 0.2)",
        gradientColors: [
          "rgba(254, 242, 242, 0.95)",
          "rgba(252, 226, 226, 0.85)",
        ],
        titleColor: "#7F1D1D",
        messageColor: "#B91C1C",
      };
    case "warning":
      return {
        iconColor: "#F59E0B",
        iconBgColor: "rgba(245, 158, 11, 0.1)",
        iconBorderColor: "rgba(245, 158, 11, 0.2)",
        gradientColors: [
          "rgba(255, 251, 235, 0.95)",
          "rgba(254, 243, 199, 0.85)",
        ],
        titleColor: "#78350F",
        messageColor: "#D97706",
      };
    case "confirm":
      return {
        iconColor: "#F59E0B",
        iconBgColor: "rgba(245, 158, 11, 0.1)",
        iconBorderColor: "rgba(245, 158, 11, 0.2)",
        gradientColors: [
          "rgba(255, 251, 235, 0.95)",
          "rgba(254, 243, 199, 0.85)",
        ],
        titleColor: "#78350F",
        messageColor: "#92400E",
      };
    case "info":
    default:
      return {
        iconColor: "#3B82F6",
        iconBgColor: "rgba(59, 130, 246, 0.1)",
        iconBorderColor: "rgba(59, 130, 246, 0.2)",
        gradientColors: [
          "rgba(239, 246, 255, 0.95)",
          "rgba(219, 234, 254, 0.85)",
        ],
        titleColor: "#1E3A8A",
        messageColor: "#2563EB",
      };
  }
};

const getToastIcon = (type: ToastType, iconColor: string) => {
  switch (type) {
    case "success":
      return (
        <View style={[styles.iconSymbol, { backgroundColor: iconColor }]}>
          <Text style={styles.iconText}>✓</Text>
        </View>
      );
    case "error":
      return (
        <View style={[styles.iconSymbol, { backgroundColor: iconColor }]}>
          <Text style={styles.iconText}>✕</Text>
        </View>
      );
    case "warning":
      return (
        <View style={[styles.iconSymbol, { backgroundColor: iconColor }]}>
          <Text style={styles.iconText}>!</Text>
        </View>
      );
    case "confirm":
      return (
        <View style={[styles.iconSymbol, { backgroundColor: iconColor }]}>
          <Text style={styles.iconText}>?</Text>
        </View>
      );
    case "info":
    default:
      return (
        <View style={[styles.iconSymbol, { backgroundColor: iconColor }]}>
          <Text style={styles.iconText}>i</Text>
        </View>
      );
  }
};

export const CustomToast: React.FC<CustomToastProps> = ({
  title,
  message,
  type,
  onPress,
}) => {
  const config = getToastConfig(type);

  return (
    <TouchableOpacity
      style={[styles.container, { width: SCREEN_WIDTH - 32 }]}
      onPress={onPress}
      activeOpacity={0.9}
    >
      <BlurView intensity={20} tint="light" style={styles.blurContainer}>
        <LinearGradient
          colors={config.gradientColors as [ColorValue, ColorValue]}
          style={styles.gradient}
        >
          <View style={styles.iconContainer}>
            <View
              style={[
                styles.icon,
                {
                  backgroundColor: config.iconBgColor,
                  borderColor: config.iconBorderColor,
                },
              ]}
            >
              {getToastIcon(type, config.iconColor)}
            </View>
          </View>

          <View style={styles.contentContainer}>
            <Text
              style={[styles.title, { color: config.titleColor }]}
              numberOfLines={1}
            >
              {title}
            </Text>
            <Text
              style={[styles.message, { color: config.messageColor }]}
              numberOfLines={4}
            >
              {message}
            </Text>
          </View>

          <View style={styles.actionContainer}>
            <View style={styles.actionDots}>
              <View
                style={[styles.dot, { backgroundColor: config.iconColor }]}
              />
              <View style={styles.dot} />
              <View style={styles.dot} />
            </View>
          </View>
        </LinearGradient>
      </BlurView>
    </TouchableOpacity>
  );
};

// Confirm Toast Component
interface ConfirmToastProps {
  title: string;
  message: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
}

export const ConfirmToast: React.FC<ConfirmToastProps> = ({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
}) => {
  const config = getToastConfig("confirm");

  return (
    <View style={[styles.container, { width: SCREEN_WIDTH - 32 }]}>
      <BlurView intensity={20} tint="light" style={styles.blurContainer}>
        <LinearGradient
          colors={config.gradientColors as [ColorValue, ColorValue]}
          style={styles.confirmGradient}
        >
          <View style={styles.confirmHeader}>
            <View style={styles.iconContainer}>
              <View
                style={[
                  styles.icon,
                  {
                    backgroundColor: config.iconBgColor,
                    borderColor: config.iconBorderColor,
                  },
                ]}
              >
                {getToastIcon("confirm", config.iconColor)}
              </View>
            </View>
            <View style={styles.confirmContentContainer}>
              <Text
                style={[styles.title, { color: config.titleColor }]}
                numberOfLines={1}
              >
                {title}
              </Text>
              <Text
                style={[styles.message, { color: config.messageColor }]}
                numberOfLines={3}
              >
                {message}
              </Text>
            </View>
          </View>

          <View style={styles.confirmButtonsContainer}>
            <TouchableOpacity
              style={[styles.confirmButton, styles.cancelButton]}
              onPress={onCancel}
              activeOpacity={0.7}
            >
              <Text style={styles.cancelButtonText}>{cancelText}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.confirmButton, styles.confirmActionButton]}
              onPress={onConfirm}
              activeOpacity={0.7}
            >
              <Text style={styles.confirmButtonText}>{confirmText}</Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </BlurView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    borderRadius: 16,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  blurContainer: {
    flex: 1,
    borderRadius: 16,
    overflow: "hidden",
  },
  gradient: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    minHeight: 80,
  },
  iconContainer: {
    marginRight: 12,
  },
  icon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 2,
  },
  iconSymbol: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  iconText: {
    color: "white",
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: FontFamilies.InterTight.SemiBold,
  },
  contentContainer: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    fontFamily: FontFamilies.InterTight.SemiBold,
    marginBottom: 4,
    lineHeight: 20,
  },
  message: {
    fontSize: 14,
    fontFamily: FontFamilies.InterTight.Regular,
    lineHeight: 18,
  },
  actionContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  actionDots: {
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "rgba(107, 114, 128, 0.3)",
  },
  // Confirm Toast styles
  confirmGradient: {
    padding: 16,
    minHeight: 120,
  },
  confirmHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  confirmContentContainer: {
    flex: 1,
    marginLeft: 12,
  },
  confirmButtonsContainer: {
    flexDirection: "row",
    gap: 12,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  cancelButton: {
    backgroundColor: "rgba(107, 114, 128, 0.15)",
  },
  confirmActionButton: {
    backgroundColor: "#EF4444",
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#4B5563",
  },
  confirmButtonText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#FFFFFF",
  },
});

// Toast configuration for react-native-toast-message
export const toastConfig = {
  customNotification: (props: any) => (
    <CustomNotificationToast
      title={props.props?.title}
      body={props.props?.body}
      onPress={props.props?.onPress}
    />
  ),
  success: (props: any) => (
    <CustomToast
      title={props.text1 || "Success"}
      message={props.text2 || ""}
      type="success"
      onPress={props.onPress}
    />
  ),
  error: (props: any) => (
    <CustomToast
      title={props.text1 || "Error"}
      message={props.text2 || ""}
      type="error"
      onPress={props.onPress}
    />
  ),
  info: (props: any) => (
    <CustomToast
      title={props.text1 || "Info"}
      message={props.text2 || ""}
      type="info"
      onPress={props.onPress}
    />
  ),
  warning: (props: any) => (
    <CustomToast
      title={props.text1 || "Warning"}
      message={props.text2 || ""}
      type="warning"
      onPress={props.onPress}
    />
  ),
  confirm: (props: any) => (
    <ConfirmToast
      title={props.text1 || "Confirm"}
      message={props.text2 || ""}
      onConfirm={props.props?.onConfirm}
      onCancel={props.props?.onCancel}
      confirmText={props.props?.confirmText}
      cancelText={props.props?.cancelText}
    />
  ),
};
